import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { CopilotStatus } from '../types'

/**
 * Copilot UI state is split into separate contexts so consumers only re-render
 * for what they read: every ConditionListItem reads highlights (changes on
 * applies) while VersionTab reads status (changes twice per turn). One
 * combined context would re-render the whole pattern tree twice per turn for
 * no visual change. All defaults are safe no-ops for consumers outside the
 * provider. (Since the AI Builder became a side-panel tab there is no
 * open/closed state here — tab selection is local to the panel owner.)
 */

interface StatusState {
  status: CopilotStatus
  setStatus: (status: CopilotStatus) => void
}
interface HighlightState {
  highlightConditionIds: Set<string>
  flashConditions: (ids: string[]) => void
}

const EMPTY_SET = new Set<string>()

const CopilotStatusContext = createContext<StatusState>({ status: 'idle', setStatus: () => {} })
const CopilotHighlightContext = createContext<HighlightState>({
  highlightConditionIds: EMPTY_SET,
  flashConditions: () => {},
})

const FLASH_DURATION_MS = 2500

export function PatternCopilotProvider({ children }: { children: React.ReactNode }) {
  const [status, setStatus] = useState<CopilotStatus>('idle')
  const [highlightConditionIds, setHighlightConditionIds] = useState<Set<string>>(EMPTY_SET)
  const flashTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const flashConditions = useCallback((ids: string[]) => {
    if (ids.length === 0) return
    setHighlightConditionIds(new Set(ids))
    if (flashTimerRef.current) clearTimeout(flashTimerRef.current)
    flashTimerRef.current = setTimeout(() => setHighlightConditionIds(EMPTY_SET), FLASH_DURATION_MS)
  }, [])

  useEffect(
    () => () => {
      if (flashTimerRef.current) clearTimeout(flashTimerRef.current)
    },
    []
  )

  const statusValue = useMemo(() => ({ status, setStatus }), [status])
  const highlightValue = useMemo(
    () => ({ highlightConditionIds, flashConditions }),
    [highlightConditionIds, flashConditions]
  )

  return (
    <CopilotStatusContext.Provider value={statusValue}>
      <CopilotHighlightContext.Provider value={highlightValue}>
        {children}
      </CopilotHighlightContext.Provider>
    </CopilotStatusContext.Provider>
  )
}

export const useCopilotStatus = () => useContext(CopilotStatusContext)
export const useCopilotHighlights = () => useContext(CopilotHighlightContext)
